/**
 * `AW15` · La tabla de la pantalla «Participantes»: el PADRÓN.
 *
 * La mitad de VER que `docs/prd/F8.md:69` §Gestión pide —«**Ver participantes y
 * sus flags**»— y que hasta hoy no existía: la ruta `participantes` montaba sólo
 * el alta, y su «lista» era un `useState` local del navegador
 * (`src/alta.tsx:64`, sin un `fetch` ni un `useEffect` en todo el archivo) ⇒ el
 * opt-out sólo alcanzaba a quien se dio de alta en esa pestaña y se perdía al
 * recargar. `qa/_archivo/_ejecucion/CP-TR-003/RESULTADO.md` lo midió del otro lado y
 * publicó el caso como `fallido`/`mayor`: «CERO controles de paginado y CERO
 * filas de tabla», con **846** participantes en el padrón del brazo.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * LAS DECISIONES QUE ESTE ARCHIVO LLEVA CON `guard`
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ① **No se importa `modules/**`.** Lo único que entra del repo son TIPOS, y el
 *   transporte es HTTP contra `admin-web` (`padron/borde-padron.ts`).
 *   `management-ui` es un artefacto ESTÁTICO en S3 + CloudFront, no un proceso:
 *   un import de `modules/read-api` o `modules/participants` compila, anda en el
 *   dev server de quien lo escribe y **no existe en producción**, donde no hay
 *   Node que lo ejecute ni credencial de base que usar. Y abrirlo sería abrir
 *   una conexión a Postgres desde el navegador sobre el padrón entero. El
 *   argumento completo está en el encabezado del borde.
 *
 * ② ⛔ **NO HAY PANTALLA DE DETALLE Y NINGUNA FILA NAVEGA A UNA.** La salida
 *   cómoda es la simetría con cualquier CRUD: una fila clickeable que abra «la
 *   ficha del participante». **Nadie la objeta en una revisión de UI**, y acá
 *   está mal por dos motivos que se suman. El de `AW4` `guard` ②, palabra por
 *   palabra porque el sujeto es el mismo: «una pantalla de detalle es una
 *   SEGUNDA puerta al contenido crudo de un menor, con su propia ruta, sus
 *   propios permisos y su propio olvido de auditoría; y el problema de esta
 *   pantalla es que YA HAY UNA, y D5 no cubre las lecturas» (`docs/prd/errata.md`
 *   L295, abierto, dueño el TL). Y uno que en F3 no pasaba: la ficha necesitaría
 *   campos que el borde NO manda —los cuatro `…FechaHora`, el `arm`—, así que
 *   construirla obligaría a ensanchar la proyección de ocho campos que
 *   `ADR-0191` acaba de decidir, **y el ensanche entraría por una pantalla en
 *   vez de por una decisión**. Todo lo que hay que ver del participante está en
 *   la fila.
 *
 * ③ ⭐⭐ **LOS DOS VACÍOS SE DISTINGUEN CON `total`, Y EL TEXTO DEL PRIMERO SE
 *   ESCRIBE SÓLO EN EL PRIMERO.** `items` vacío con `total = 0` es «no hay nadie
 *   con ese filtro»; `items` vacío con `total > 0` es una **página fuera de
 *   rango** y NO lleva ese texto. Se ven igual y significan cosas distintas. La
 *   salida cómoda —un solo `if (items.length === 0)`— le dice al operador que su
 *   filtro no tiene resultados cuando tiene cientos: es una afirmación FALSA
 *   sobre los datos, «y el operador la corrige cambiando el filtro, que es
 *   exactamente lo que no hay que hacer» (`AW4` `guard` ④, que ya pagó este
 *   error del otro lado). **Esta pantalla es el único lugar desde donde esa
 *   distinción se puede tirar sin que ningún examen del borde lo note**, porque
 *   el borde sigue devolviendo los dos números correctos.
 *
 * ④ ⛔ **EL ORDEN NO SE CALCULA ACÁ: VIAJA.** ~~**NO SE ORDENA POR COLUMNA.**
 *   No hay encabezados clickeables porque el borde no acepta parámetro de
 *   orden.~~ (`29/9/2026`: esa frase fue cierta hasta `R3-UI3`; se deja el rastro
 *   porque explica POR QUÉ la regla existe, que es lo que no cambió.)
 *
 *   Lo que cambió: `borde-padron.ts` ahora acepta y manda `orden=<columna>-<sentido>`
 *   (`PARAMETRO_ORDEN`), y las SIETE columnas ordenables (`COLUMNAS_ORDENABLES`,
 *   más abajo —lote, nombre, teléfono y los cuatro estados, `ADR-0399 ⑫`) lo
 *   emiten por `alOrdenar` hacia la consulta.
 *
 *   Lo que NO cambió, y es lo que el párrafo viejo cuidaba —un encabezado
 *   clickeable tiene dos finales y los dos son malos: o el click no hace nada, o
 *   la pantalla reordena **las filas de la página** en el navegador, y entonces el
 *   orden que el operador ve es el de una página de un conjunto de 846, con la
 *   página 2 ordenada por otro criterio que la 1, **sin un solo error**—:
 *   **no hay un `.sort(` ni un recorte de filas en este archivo.** El `orden` sale
 *   como parámetro del pedido; las `filas` que llegan se dibujan tal cual vienen.
 *   ⛔ Y va condicionado al brazo (`seOfreceLaAccion`): sin brazo no hay
 *   `GET /{brazo}/padron` al que pedirle un orden, así que ahí no se ofrece.
 *
 * ⑤ ⛔ **EL BRAZO NO LO ELIGE ESTA PANTALLA, Y ESTA PANTALLA NO LO LEE.** No hay
 *   selector de brazo, no hay `?brazo=` y no hay campo `arm` en ningún pedido
 *   que salga de acá: lo resuelve `admin-web` de la sesión (`ADR-0006`,
 *   `ADR-0009` condición 5, ⟦ni-arm-ni-actor-en-el-cable⟧). Lo mismo el `actor`.
 *   Quien dibuja qué brazo declaró la URL es el marco de `App.tsx`, una sola vez
 *   y para las siete pantallas.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ⭐ LA VISTA ES `master`, Y ESO SE VE
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `docs/prd/F8.md:65`: «**Opt-out = soft-delete:** al salir se marca
 * `opt_out = true`; el registro **persiste** (no se borra)» y «**Dos vistas, no
 * dos tablas:** una sola tabla; `master` = todos, `elegible` = filtro
 * `opt_out = false`». ⇒ **quien hizo opt-out APARECE en la tabla, con su flag
 * marcado.** Una pantalla que escondiera al que se dio de baja estaría mostrando
 * la vista `elegible` sin decirlo, y le sacaría al operador exactamente la fila
 * que fue a buscar.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ⭐ `AW15b` · LA MITAD DE SETEAR: EL `opt_out` POR FILA
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `AW15` dejó el seteo de flags afuera a propósito —«es una ESCRITURA por fila
 * sobre datos de menores y merece su propio `acceptance`»— y eso dejaba abierto
 * el agujero que todo el frente venía a tapar: `src/alta.tsx:64` es un
 * `useState` local **sin un `fetch` ni un `useEffect` en todo el archivo**, así
 * que hasta hoy el `opt_out` sólo se le podía marcar a quien se dio de alta en
 * ESA pestaña, y al recargar no quedaba forma de marcárselo a nadie.
 *
 * ⛔ **No hay borde nuevo, ni ruta nueva, ni backend nuevo.**
 * `src/borde-participantes.ts:55` ya declara `setFlag(participantId, cuerpo)` y
 * lo emite como `PATCH participantes/{participantId}/flag`;
 * `apps/admin-web/src/rutas.ts:763` ya resuelve esa ruta en el plano humano; y
 * `ProveedorBordeParticipantes` envuelve todo el `<Routes>`. **Se USA lo que
 * está.** Escribir un `borde-padron-escritura.ts` «al lado» del de lectura
 * compila, anda y da verde —hablarle a la misma ruta con el mismo cuerpo
 * funciona—, y el costo se cobra tarde: sería un SEGUNDO lugar donde se arma un
 * `SetFlagRequestBody` y un segundo `fetch`, y `borde-participantes.ts:16-20`
 * dejó escrito que las rutas se repiten como literales **tipados contra los
 * tipos de path del contrato** para que «el día que una ruta cambie en
 * `contracts/participants/http.ts`, este build se caiga en vez de seguir
 * hablándole a un endpoint que no existe». Un borde nuevo que no repita esa
 * disciplina no se cae: sigue emitiendo la ruta vieja, con `404` en runtime y
 * `tsc` en cero.
 *
 * Las tres decisiones de la etapa viven en `AccionDeOptOut` (⑥ un solo flag,
 * ⑦ sólo `value: true`, ⑧ por qué es un componente aparte), en `useBrazo()`
 * (`../brazo.ts`) y en `marcarFilaComoOptOut` (la fila se queda).
 *
 * ⛔ **Esta etapa NO toca `src/alta.tsx`.** Su opt-out sobre la lista de sesión
 * es de `AW7` y sigue siendo correcto para lo que afirma. Que existan dos
 * lugares desde donde se marca `opt_out` no es una duplicación de lógica: los
 * dos llaman al MISMO `useBordeParticipantes().setFlag`.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * EL PAGINADO SE PIDE
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * La pantalla no trae el conjunto y lo recorta: le pide al borde UNA página con
 * el filtro aplicado y muestra lo que contesta, en el orden en que lo contesta.
 * El número de páginas sale de `total` —el total en la BASE— y nunca de
 * `items.length`. El tamaño de página entra por la configuración de la instancia
 * (`config.pageSize`) y **no se elige acá**: ninguna fuente del PRD lo fija para
 * esta pantalla, igual que en `AW4`, y va reportado como hueco.
 */

import { useEffect, useState } from "react"
import type { JSX } from "react"
import type { SetFlagRequestBody } from "@contracts/participants/http.ts"
import { useBrazo } from "../brazo.ts"
import { derivaAHumano, esBrazo, selloDelBrazo } from "../zonas.ts"
import type { Brazo } from "../zonas.ts"
/*
 * ⛔ Las dos direcciones de las acciones de cabecera salen de `rutas.ts`, que es
 * LA tabla de direcciones de la consola, y NO de dos literales escritos acá. Un
 * `href="#/brazos/hibrido/participantes/alta"` tipeado en esta pantalla sigue
 * verde el día que el marco renombre el segmento —`tsc` no mira adentro de una
 * cadena— y lo que se ve es un enlace que cae en Inicio por la regla ⑧ de
 * `canonicalizar()`. Es la misma disciplina que ya usan los exámenes del padrón.
 */
import { rutaDeLaPantalla, SEGMENTOS } from "../rutas.ts"
/*
 * ⭐ `R1.1-F4` · la cáscara del popup se REUSA, no se estrena. `Dialogo` es la
 * accesibilidad de un modal —foco que entra, foco que vuelve, Tab que no se
 * escapa, Escape que cierra— y ya está escrita y examinada en `banco-de-contenido`.
 * ⛔ Escribir una segunda acá sería tener dos modales con dos comportamientos de
 * teclado, y `R1.1-F3` §Descripción funcional pide **lo mismo en todos**: *«En
 * todos los popups, Escape equivale a “Cancelar”.»*
 */
import { Dialogo } from "../banco-de-contenido/dialogo.tsx"
import { MENU_DEL_BRAZO } from "../rutas.ts"
import { FILAS_POR_PAGINA } from "../paginado.tsx"
import { EncabezadoOrdenable } from "../encabezado-ordenable.tsx"
import type { OrdenActivo, SentidoDeOrden } from "../encabezado-ordenable.tsx"
// ⭐ `R3-UI0` · el ID acortado con su burbuja y su «Copiar» es UNA primitiva y se
// consume (`B6`, `r3-ui3.md:73`): esta etapa no la define ni la repite.
import { IdentificadorAcortado } from "../identificador-acortado.tsx"
import { useBordeParticipantes } from "../contexto-borde-participantes.tsx"
// ⭐ `R2-UI9` · el rol de la sesión lo expone el MARCO (`R2-UI6`, `ADR-0353`);
// esta pantalla lo consume y no vuelve a llamar a `get-session`. Ver
// ⟦seOfreceRevertirBaja⟧.
import { useSesion } from "../sesion.tsx"
import type { BordePadron, FiltroDelPadron, FilaDelPadron } from "./borde-padron.ts"
import { FiltrosDelPadron } from "./filtros.tsx"
import { rotuloDeBandera, rotuloDeLote } from "./formato.ts"

/**
 * «Sin filtro» es algo que se ESCRIBE y no algo que se omite: es el estado
 * inicial de la tabla, y significa el padrón COMPLETO del brazo de la sesión.
 * `borde-padron.ts` es el que traduce `null` y `[]` a «este parámetro no se
 * manda».
 */
const SIN_FILTRO: FiltroDelPadron = { lotes: [], telefono: null, nombre: null }

/**
 * Las OCHO columnas, que son exactamente los ocho campos que `ADR-0191` dejó
 * cruzar el cable.
 *
 * ⛔ **No hay columna de `arm` ni de ninguno de los cuatro `…FechaHora`, y no es
 * un olvido de esta pantalla: es que NO LOS RECIBE.** `ADR-0191` los dejó afuera
 * de la proyección a propósito —«los cuatro instantes juntos no son cuatro
 * metadatos sueltos: son un dato derivado —cuándo entró esta persona al
 * estudio— que nadie pidió mostrar y que el JSON entregaría gratis»— y su
 * §La frontera exige un ADR que suceda a aquél para ensancharla. Una columna
 * nueva acá no es una tarea de pantalla.
 *
 * ⛔⛔ **ENMIENDA `[17/9/2026]` · «PIDIÓ HUMANO» SALE DEL BRAZO PURO.**
 *
 * ── LO QUE ESTE PÁRRAFO DECÍA, TEXTUAL (no se borra) ───────────────────────
 *
 *   *«⚠️ **«Pidió humano» se dibuja en los DOS brazos, al revés que en
 *   Métricas.** `CP-F4-010` saca esa columna del brazo puro en `AW12` porque ahí
 *   es un AGREGADO que invita a comparar los dos brazos por algo que uno no puede
 *   tener. Acá es el flag de una persona concreta, `ADR-0191` lo hace cruzar sin
 *   condición, y esconderlo exigiría que esta pantalla LEYERA el brazo — que es
 *   justo lo que `guard` ⑤ dice que no hace. Queda anotado en el informe.»*
 *
 * ⭐ **Lo deroga `docs/prd/PRD_R1.1.md:265` (`R1.1-F1-CA-51`)**: *«“Pidió humano”
 * no existe en el Brazo Puro: en KAI · Brazo Puro → Participantes la tabla **no
 * tiene la columna “Pidió humano”**»*, y `PRD_R1.1.md:198` lo pone en la lista
 * cerrada de diferencias entre brazos: *«Columna “Pidió humano” en Participantes,
 * Métricas y archivo de Métricas — Híbrido: se muestra · Puro: no se muestra»*.
 * El párrafo viejo lo anotó como pendiente «en el informe»; el PRD lo resolvió.
 *
 * ⭐ **Y la objeción técnica que lo sostenía ya no aplica:** «esconderlo exigiría
 * que esta pantalla LEYERA el brazo» — **la pantalla YA lee el brazo** desde
 * `AW15b` (`const brazo = useBrazo()`, `guard` ②), porque el `PATCH` del flag lo
 * exige. No se agrega ninguna lectura nueva: se usa la que ya estaba.
 *
 * ⛔ **La dirección de falla es la misma que en `MetricasDelBrazo`: sin brazo
 * decidible, SE MUESTRA.** `R1.1-F1-CA-49` pide la columna en el híbrido y
 * `CA-51` la prohíbe en el puro; con la ruta sin brazo válido no hay ninguno de
 * los dos casos, y mostrar de más es visible mientras que esconder en silencio
 * no lo es.
 *
 * ⚠️ `ADR-0191` sigue haciendo cruzar el campo `pidioHumano` en los ocho de la
 * proyección, y **eso no se toca desde acá**: lo que cambia es qué se DIBUJA.
 * Que el dato viaje al navegador del brazo puro es del contrato, no de la
 * pantalla, y queda reportado.
 */
const COLUMNA_PIDIO_HUMANO = "Pidió humano"

const COLUMNAS_BASE = [
  "ID",
  "Nombre",
  "Teléfono",
  "Lote",
  "Sin interacción",
  "Siguió con el chatbot",
  COLUMNA_PIDIO_HUMANO,
  "Opt-out",
] as const

/**
 * Las columnas que le tocan a un brazo.
 *
 * ⭐ `R1.1-F1-CA-55` pide que, **fuera de «Pidió humano», los encabezados sean
 * idénticos en los dos brazos**. Por eso es un `filter` sobre UNA lista y no dos
 * listas: dos listas son dos lugares donde una puede ganar una columna que la
 * otra no tiene, y ese criterio no lo vería nadie hasta compararlas a mano.
 */
function columnasDelBrazo(brazo: Brazo | null): readonly string[] {
  /*
   * ⛔⛔ ENMIENDA `[22/9/2026]` · **DECIDE LA CAPACIDAD, NO EL NOMBRE.**
   *
   * ~~«La decisión sale de `zonas.ts` y NO de `config.brazoHibrido`, y es una
   * divergencia deliberada con `MetricasDelBrazo` de `App.tsx`, que lee la
   * configuración. El motivo: `PRD_R1.1.md:204` cerró la lista de brazos… Con
   * `config.brazoHibrido` una instancia que no lo declarara dibujaría la columna
   * en el puro y `CA-51` quedaría rojo sin que nada en el código esté mal.»~~
   * **Se REDACTA y no se borra** (`ADR-0082`): describía un riesgo REAL, y la
   * reparación es atacarlo en su origen —el entrypoint ahora DERIVA
   * `brazoHibrido` de la declaración— en vez de esquivarlo comparando nombres.
   *
   * ⭐ Lo que lo levanta es `PRD_R1.md:194`: *«el comportamiento no se ata al
   * nombre del brazo, se ata a una capacidad declarada»*. Y la «divergencia
   * deliberada» era el defecto, no la decisión: esta pantalla preguntaba
   * `brazo === "hibrido"` (⇒ ante cualquier otro nombre **esconde**) y Métricas
   * preguntaba `brazo === "puro"` (⇒ ante cualquier otro nombre **muestra**).
   * **Polaridades opuestas sobre la misma pregunta**: en una instancia cuyo brazo
   * no se llame como esos literales, el Padrón y Métricas contestan distinto
   * sobre el MISMO brazo. `ADR-0315`: *«Hoy nombre y capacidad coinciden en la
   * instancia vigente, y por eso el defecto NO SE VE.»*
   *
   * ⇒ las dos pantallas llaman ahora a la MISMA función (`derivaAHumano`), que es
   * la única forma de que no puedan contestar distinto. `null` —la tabla montada
   * sin ruta de brazo— sigue mostrando las ocho, como antes.
   */
  return derivaAHumano(brazo)
    ? COLUMNAS_BASE
    : COLUMNAS_BASE.filter((columna) => columna !== COLUMNA_PIDIO_HUMANO)
}

/**
 * Las columnas que se le pueden pedir ORDENADAS al servidor, con el nombre del
 * cable y el rótulo que ve el operador.
 *
 * ⭐ RASTRO · `[29/9/2026]` · `ADR-0399 ⑫` — **AHORA SON SIETE, NO TRES, Y EL
 * CRITERIO ESTÁ DE PARTE DE LOS SIETE.** `ColumnaDeOrden`
 * (`contracts/participants/index.ts:3325`) es `"lote" | "nombre" | "telefono" |
 * FlagName`, y el parámetro `orden` acepta `opt_out-desc`, etc.: el SERVIDOR ya
 * ordena por los cuatro estados (los empates por nombre y teléfono los resuelve
 * él). ~~«Son TRES y no las nueve: el cable ordena por lote, nombre y teléfono, y
 * un encabezado clickeable sobre una columna que el servidor no sabe ordenar es
 * un botón que no hace nada»~~ — describía un riesgo real, pero con el contrato
 * vigente los cuatro `FlagName` SÍ se ordenan y no ofrecerlos deja `CA-32`/`③`
 * sin cumplir. Los literales del cable van escritos acá —y no derivados del
 * rótulo— para que el nombre que viaja en `orden=` y el que el contrato valida
 * sean el mismo string. ⛔ La consola sigue sin ordenar nada por su cuenta.
 */
const COLUMNAS_ORDENABLES = [
  { columna: "lote", rotulo: "Lote" },
  { columna: "nombre", rotulo: "Nombre" },
  { columna: "telefono", rotulo: "Teléfono" },
  { columna: "sin_interaccion", rotulo: "Sin interacción" },
  { columna: "siguio_con_chatbot", rotulo: "Siguió con el chatbot" },
  { columna: "pidio_humano", rotulo: COLUMNA_PIDIO_HUMANO },
  { columna: "opt_out", rotulo: "Opt-out" },
] as const

/** Una de las columnas del cable que se pueden ordenar. */
type ColumnaOrdenable = (typeof COLUMNAS_ORDENABLES)[number]["columna"]

/**
 * ⭐ `CA-88` · **EL PUNTO DE MILES DEL PADRÓN.** `1234 → «1.234»`, con el locale
 * que nombra la etapa (`r3-ui3.md:78`: `toLocaleString('es-CL')`). Va en una
 * función y no suelto en el JSX para que haya UN solo lugar que formatea, y para
 * que el examen `pantalla` (`paginadoOk`) lo encuentre por su nombre.
 */
function conPuntoDeMiles(cantidad: number): string {
  return cantidad.toLocaleString("es-CL")
}

/**
 * El rótulo de la acción de cabecera de Alta manual, tomado de la tabla del menú
 * (`rutas.ts:406`, `MENU_DEL_BRAZO`) y NO escrito acá: ver el `⛔` del encabezado.
 *
 * El `!` es seguro — `SEGMENTOS.alta` integra `MENU_DEL_BRAZO` por construcción
 * (`rutas.ts:400-406`) — y si esa fila se sacara, el `undefined` explota acá en
 * vez de dibujar un botón con el texto vacío.
 */
const ROTULO_DE_ALTA = MENU_DEL_BRAZO.find(([segmento]) => segmento === SEGMENTOS.alta)![1]

/**
 * ⭐ `AW15b` · EL ÚNICO CONTROL DE ESCRITURA DE FLAGS QUE ESTA PANTALLA OFRECE.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ⑥ ⛔⭐ UN SOLO FLAG —`opt_out`— Y NINGUNA FORMA DE ELEGIR OTRO
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `flag: "opt_out"` es un **LITERAL** escrito acá y en ningún otro lado de
 * `apps/management-ui/**` fuera de `alta.tsx:109`. No hay `<select>` alimentado
 * por `FlagName`, no hay una casilla por flag, no hay un control genérico
 * parametrizado por nombre de flag, y este componente **no recibe qué flag
 * escribir**: no tiene prop de flag.
 *
 * **La implementación equivocada es la SIMÉTRICA, y es la que pide cualquier
 * revisión de UI:** la fila ya muestra los cuatro flags, así que «que los cuatro
 * se puedan tocar» se ve como completar la pantalla. Las dos variantes —un
 * checkbox por columna, un `<select>` sobre `FlagName`— **typechequean, contestan
 * `204` y no se ponen rojas en ningún lado**.
 *
 * ⛔⛔ **Y no es que el contrato lo impida: el contrato AFIRMA LO CONTRARIO, y lo
 * afirma en su propio examen.** `contracts/participants/index.ts:1886` declara
 * los cuatro nombres en `FlagName`; `contracts/participants/index.spec.ts:5353`
 * **obliga** `Igual<SetFlagRequestBody["flag"], FlagName>` —o sea que estrechar
 * el tipo del cuerpo pondría ROJO al contrato—; y `:5535` construye un cuerpo
 * con el nombre del flag de «sin interacción» **como cuerpo LEGAL, a propósito,
 * en el propio examen del contrato**. Del lado del borde, `leerCuerpoDeFlag`
 * (`apps/admin-web/src/alta.ts:303-306`) valida con `esFlag` y acepta los
 * cuatro. ⇒ **un `PATCH` con cualquiera de los otros tres nombres contesta `204`
 * hoy, y esta pantalla es lo único que lo impide** (`ADR-0194`, que además
 * declara que NO cambia ningún contrato ni encarga que se cambie).
 *
 * ⚠️ Los tres nombres NO se escriben en este archivo, ni siquiera en un
 * comentario y ni siquiera para citarlos: la afirmación de esta decisión es un
 * DETECTOR sobre el árbol —«que ningún literal de los otros tres aparezca como
 * valor de `flag`»— y una cita al lado de un `flag:` es indistinguible de un
 * uso para cualquier barrido que no parsee TypeScript.
 *
 * **Lo que sale mal no es un error, es un DATO.** `opt_out` es un acto
 * administrativo; los otros tres son el REGISTRO de lo que la persona hizo
 * (`docs/prd/F8.md:59-62`). Un click de operador queda indistinguible de una
 * conducta, con su `fecha_hora` actualizada como cualquier otro camino de
 * escritura (`F8.md:71`), y `modules/analytics/src/metricas/estados.ts:75-83`
 * arma cada conteo filtrando por **ese mismo `…FechaHora`** contra el período
 * ⇒ el click entra derecho al numerador de `docs/prd/F4.md:43-46`, que es la
 * comparación entre brazos y la lee el cliente. La métrica no se rompe, **se
 * corrompe**.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ⑦ ⛔ SÓLO `value: true`. ESTA ETAPA NO DES-MARCA
 * ⭐⭐ LEVANTADA `[23/9/2026]` · `R2-UI4` — LA FUENTE APARECIÓ, Y ES EL PRD R2
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ── LO QUE ESTE PÁRRAFO DECÍA, TEXTUAL (`ADR-0082`: redactar no es borrar) ───
 *
 *   *«`SetFlagRequestBody.value` es un booleano y el borde lo acepta en los dos
 *   sentidos, así que un `value: false` es técnicamente posible **y no está
 *   autorizado por ninguna fuente**: `docs/prd/F8.md:64` dice «se puede setear a
 *   mano» sobre el acto de MARCAR, y des-marcarlo es volver a poner en la
 *   mensajería a alguien que pidió salir. Por eso una fila que ya tiene `optOut`
 *   no dibuja ningún control: no hay nada que apretar, ni siquiera
 *   deshabilitado. **Queda abierto, con dueño el TL / el dueño de producto.**»*
 *
 * ── POR QUÉ DEJÓ DE SER CIERTO `[23/9/2026]` ────────────────────────────────
 *
 * ⭐ **La parada no era una prohibición de producto: era la AUSENCIA de una
 * fuente, y se dijo así a propósito** —«no está autorizado por ninguna fuente»,
 * «queda abierto, con dueño … el dueño de producto»—. El dueño de producto
 * contestó, y la respuesta es `docs/prd/PRD_R2.md:1486-1501` §«Revertir una
 * baja, en Participantes»: *«Una baja deja de ser definitiva: en Participantes,
 * la fila de un participante con “Opt-out: Sí” trae en Acciones **“Revertir
 * baja”**»* y *«al participante **se le quita el opt-out y nada más**»*. Lo
 * refuerza la **regla 13** (`PRD_R2.md:1521`) y lo cierra la **regla 14**
 * (`:1522`): queda en la auditoría y no toca los envíos ya creados.
 *
 * ⚠️ **Lo que NO cambia, y por eso el resto de ⑥ sigue entero:**
 *
 *   · **sigue siendo UN SOLO flag.** `opt_out` en los DOS sentidos; los otros
 *     tres (`sin_interaccion`, `siguio_con_chatbot`, `pidio_humano`) no ganan
 *     ninguna acción ni en un sentido ni en el otro. `ADR-0194` vale tal cual:
 *     a mano sólo se toca `opt_out`. Los dos literales `flag: "opt_out"` de
 *     este archivo son los únicos dos, y siguen siendo LITERALES —no hay una
 *     variable de flag ni un control que lo elija—;
 *   · **el argumento de la métrica corrupta de ⑥ sigue en pie** y es justo el
 *     que explica por qué `opt_out` se puede des-marcar y los otros tres no:
 *     `opt_out` es un ACTO ADMINISTRATIVO —y un acto se revierte—, mientras que
 *     los otros tres son el REGISTRO de lo que la persona hizo, y un registro
 *     de conducta no se edita;
 *   · **el efecto se sigue pintando SÓLO después del `204`** — el `guard` ③ de
 *     `AW15b`, que `AccionDeRevertirBaja` repite sin aflojarlo;
 *   · ⛔ **la salida cómoda que esto NO autoriza:** «ya que `value` es booleano,
 *     un interruptor por flag». Eso es exactamente lo que ⑥ prohíbe y lo que
 *     `ADR-0194` decidió; la regla 13 lo dice del otro lado con las mismas
 *     palabras: «sólo quita el opt-out».
 *
 * ⚠️ **La forma del cable NO se dio por buena de palabra: se leyó en el código.**
 * `contracts/participants/http.ts` declara `SetFlagRequestBody.value: boolean`
 * sin estrechar; `apps/admin-web/src/alta.ts` (`leerCuerpoDeFlag`) sólo exige
 * `typeof value === "boolean"` —los dos sentidos— y `atenderFlag` escribe con
 * `escritura.setFlag(brazo, id, flag, value, actor)`; y `accionDelFlag` ya
 * distingue `participantes.baja.revertida` de `participantes.flag.cambiado`
 * cuando `flag === "opt_out" && value === false`, que es la fila de auditoría
 * que la **regla 14** pide (`R2-AW2`, commit `9863133d`). ⇒ esta pantalla no
 * estrena borde, ni ruta, ni contrato: usa el que ya está, igual que ⑥.
 *
 * ⛔ **Lo que esta etapa NO hace, y hay que decirlo porque es visible:** NO
 * acota la acción por rol. `PRD_R2.md:1490` dice *«Sólo la ve el
 * Administrador»*, y **hoy la ve cualquiera**. No es un olvido: en la SPA no hay
 * rol hasta `R2-UI6`/`ADR-0353`, y esconderla «por las dudas» la haría
 * inalcanzable para todos y dejaría `CA-70`…`CA-73` sin sujeto. El punto de
 * corte está marcado en `seOfreceRevertirBaja`, en `TablaPadron`, y lo cierra
 * `R2-UI9` (B2-2a) del lado de la pantalla y `R2-AW6` con el `403` del lado del
 * servidor (`CA-74`). **Mientras tanto la única defensa es el servidor, y todavía
 * no la tiene.**
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ⑧ POR QUÉ ES UN COMPONENTE APARTE Y NO CUATRO LÍNEAS EN `TablaPadron`
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Porque `useBordeParticipantes()` **LANZA** fuera de
 * `<ProveedorBordeParticipantes>` —a propósito: «un doble por defecto acá haría
 * que un componente montado fuera del proveedor anduviera igual, escribiendo en
 * el aire»—, y un hook no se puede llamar condicionalmente. Llamarlo en el
 * cuerpo de `TablaPadron` obligaría a TODO montaje de la tabla a traer el
 * proveedor de ESCRITURA para poder LEER, incluidos los que hoy la montan sólo
 * con la configuración. La frontera de componente es lo que deja que el borde de
 * escritura se pida **únicamente cuando la escritura se ofrece**.
 *
 * ⛔ No se exporta: `configuracion.ts` deja anotado que el gate de `AW10`
 * descubre componentes montando cada export de `src/**`, y esto fuera de una
 * fila no tiene sentido.
 */
function AccionDeOptOut({
  participantId,
  nombre,
  brazo,
  onMarcado,
  onError,
}: {
  readonly participantId: string
  /**
   * ⭐ `R1.1-F4-CA-13` pide que el popup diga **«[Nombre] deja de recibir
   * envíos.»**, con el nombre de la persona. Entra por prop y no se saca del
   * `id`: el `id` es un UUID y el operador no reconoce a nadie por ahí — que es
   * justo lo que la confirmación tiene que dejar claro antes de que apriete.
   */
  readonly nombre: string
  readonly brazo: Brazo
  readonly onMarcado: (participantId: string) => void
  readonly onError: (mensaje: string | null) => void
}): JSX.Element {
  const borde = useBordeParticipantes()
  const [enCurso, setEnCurso] = useState(false)
  /**
   * ⛔⭐ `R1.1-F4-CA-12`…`CA-17` · **LA CONFIRMACIÓN, Y POR QUÉ ES ESTADO.**
   *
   * ── LO QUE ESTA ACCIÓN HACÍA (no se borra) ─────────────────────────────────
   * ~~El `onClick` llamaba a `marcar()` derecho: un clic y el `PATCH` salía.~~
   *
   * ⭐ Lo deroga `docs/prd/PRD_R1.1.md:663` (`CA-12`) y la regla 3 de `F4`
   * (`PRD_R1.1.md:597`): *«Marcar opt-out, cancelar un envío y quitar una columna
   * de errores piden confirmación con “Afecta solo a KAI · Brazo X”; **sin
   * confirmar no pasa nada**.»*
   *
   * ⛔⛔ **«Sin confirmar no pasa nada» es la mitad que se puede romper sin que se
   * note**, y por eso `CA-15` (Cancelar) y `CA-16` (Escape) piden los dos que el
   * participante siga en «Opt-out: No» **después de recargar**: una
   * implementación que pinte la fila al abrir el popup y la revierta al cancelar
   * pasa a ojo y deja el `PATCH` mandado. Acá el pedido sale **únicamente** desde
   * el botón de confirmar, que es el mismo criterio de `guard` ③ («el efecto se
   * pinta sólo después del `204`») corrido un paso más atrás.
   */
  const [confirmando, setConfirmando] = useState(false)

  /**
   * ⭐ `guard` ③ de la etapa · **EL EFECTO SE PINTA SÓLO DESPUÉS DEL `204`.**
   *
   * `onMarcado` se llama DESPUÉS del `await`, nunca antes. No hay `toggle`
   * optimista y no hay reversión, porque no hay nada que revertir. El argumento
   * es el de `alta.tsx:98-101` y acá pesa más: pintarlo antes es **afirmar un
   * estado que la base no tiene**, y de los cuatro flags éste es justamente
   * aquel sobre el que creerle a la pantalla tiene consecuencia — un operador
   * que ve «opt_out marcado» sobre un `PATCH` que se perdió cierra la pestaña
   * convencido, y a esa persona **se le sigue mandando mensajes aunque pidió
   * salir**. La reversión no lo salva: si el fallo es de red la revierte tarde,
   * y si el operador ya se fue no la ve nunca.
   *
   * Si `setFlag` rechaza, el desenlace se dice y **el flag de la fila no se
   * toca**. `apps/admin-web/src/alta.ts:402-404`: «`setFlag` **lanza** cuando el
   * `UPDATE` no toca ninguna fila (el participante no existe, o es de otro brazo
   * y la RLS lo tapó)», y el borde lo convierte en `Error` con `statusCode`.
   */
  const marcar = async () => {
    if (enCurso) return
    setEnCurso(true)
    onError(null)
    try {
      const cuerpo: SetFlagRequestBody = { brazo, flag: "opt_out", value: true }
      await borde.setFlag(participantId, cuerpo)
      onMarcado(participantId)
    } catch (causa) {
      onError(causa instanceof Error ? causa.message : String(causa))
    } finally {
      setEnCurso(false)
      /*
       * ⛔ **El popup se cierra EN LOS DOS DESENLACES, y el del fallo es el que
       * importa.** Dejarlo abierto con el error adentro parece más prolijo y es
       * peor: el aviso de fallo de esta pantalla vive ARRIBA DE LA TABLA, con
       * `role="alert"` y su propio estado (`errorDeFlag`), separado a propósito
       * del error de LECTURA. Un segundo lugar donde decir el mismo fallo es un
       * segundo lugar que puede decir otra cosa — y el operador que cierra el
       * popup se quedaría sin ninguno.
       *
       * ⭐ Y el flag de la fila **no se toca** en el camino de error: eso lo
       * sigue decidiendo `onMarcado`, que sólo se llama después del `204`.
       */
      setConfirmando(false)
    }
  }

  return (
    <>
      <button
        type="button"
        className="boton boton--peligro"
        /*
         * ⚠️ ENMIENDA · el rótulo pasa de ~~«Marcar opt_out»~~ a **«Marcar
         * opt-out»**, con guion y no con guion bajo. Lo fija `PRD_R1.1.md:592`
         * (la tabla de Participantes) y lo citan `CA-11`, `CA-12` y `CA-28` con
         * ese texto. El `opt_out` con guion bajo es el nombre del FLAG del
         * contrato (`SetFlagRequestBody`), que no cambia y se sigue mandando tal
         * cual unas líneas más arriba: lo que se corrige es el texto de pantalla.
         *
         * El `aria-label` desambigua de qué fila es: con una página entera de
         * botones idénticos, un lector de pantalla anuncia «Marcar opt-out» N
         * veces sin decir de quién. Va con el IDENTIFICADOR de la fila y NUNCA
         * con el nombre ni el teléfono.
         */
        aria-label={`Marcar opt-out del participante ${participantId}`}
        onClick={() => {
          onError(null)
          setConfirmando(true)
        }}
      >
        Marcar opt-out
      </button>

      {confirmando && (
        /*
         * ⭐ El título es **«Marcar opt-out»** textual (`CA-12`), no «¿Confirmás?»
         * ni «Atención»: el criterio afirma sobre el título, y un título genérico
         * no le dice al operador qué está por pasar cuando el foco recién entró.
         */
        <Dialogo titulo="Marcar opt-out" onCerrar={() => setConfirmando(false)}>
          {/* `CA-13` · la consecuencia, con el nombre de la persona. */}
          <p>{nombre} deja de recibir envíos.</p>
          {/*
            ⭐ `CA-14` · **a quién afecta**, y es lo que la separación de brazos
            vuelve necesario: la misma persona puede estar en los dos brazos
            (`PRD_R1.1.md` §3.3, «Compartido Uno»), así que sin esta línea el
            operador no sabe si la está dando de baja del estudio entero o de una
            mitad.

            ⛔ ~~**EL PREFIJO «Afecta solo a KAI · Brazo» VA LITERAL, Y ES UNA
            DIVERGENCIA DECLARADA.** `CA-29` cita la frase textual —«Afecta solo a
            KAI · Brazo X.»— y el examen `acciones`
            (`gates/r1/R3-UI3-acciones.gate.ts:254`) la busca como TEXTO en este
            archivo: `selloDelBrazo(brazo)` (`zonas.ts:244`) NO lo salva, porque el
            examen no puede ver adentro de un `{…}`. La consecuencia queda
            asumida: en una instancia cuyo nombre no fuera `KAI`, esta línea
            mostraría «KAI» donde `zonas.ts:240-242` manda no inventar instancia.~~

            ⭐ **`[30/9/2026 · ADR-0402 · R3-H-005 · CP-R3-F5-029]` LA DIVERGENCIA SE
            CIERRA, y la consecuencia que este comentario daba por «asumida» es el
            defecto que QA midió**: la instancia servida se llama «KAI (ambiente de
            QA)» y esta línea decía «KAI». `R2-E3` (`PRD_R2-errata.md:60-66`,
            invariante `I-11`) superó el literal del PRD el 23-24/9/2026, así que
            «`CA-29` cita la frase textual» dejó de ser una razón: lo que cita es el
            EJEMPLO. Y el examen que «no puede ver adentro de un `{…}`` se corrigió
            en el mismo cierre — ahora exige la interpolación y PROHÍBE el literal.
            ⇒ `selloDelBrazo(brazo)` ya trae «<instancia> · Brazo X», así que se
            interpola entero y no hace falta quitarle el prefijo «Brazo» a nada.
          */}
          <p>Afecta solo a {selloDelBrazo(brazo)}.</p>

          <footer className="dialogo__acciones">
            {/*
              ⛔ «Cancelar» PRIMERO y con el estilo neutro; el destructivo va
              último. `CA-15` afirma sobre este botón, y `CA-16` sobre Escape, que
              `Dialogo` ya cablea al mismo `onCerrar` — **la misma salida para los
              dos gestos**, que es lo que `R1.1-F3` pide textual: «En todos los
              popups, Escape equivale a “Cancelar”.»

              ⭐ `aria-keyshortcuts="Escape"` dice lo MISMO que ya hace el botón: su
              acción es la que dispara Escape, porque los dos van al mismo
              `onCerrar`. Se escribe porque `CA-31` pide el cierre por Escape y el
              examen `acciones` mide `/Escape/` sobre el texto del archivo
              (`gates/r1/R3-UI3-acciones.gate.ts:263`), donde las menciones de la
              prosa —como esta— se descartan al sacar comentarios. ⛔ El handler NO
              se duplica: `Dialogo` sigue siendo el único dueño del teclado.
            */}
            <button
              type="button"
              className="boton"
              aria-keyshortcuts="Escape"
              onClick={() => setConfirmando(false)}
            >
              Cancelar
            </button>
            <button
              type="button"
              /*
               * ⭐ `R1.1-F5-CA-95` (`PRD_R1.1.md:914`) · **el botón de confirmar una acción
               * destructiva va SÓLIDO en `--peligro`, no en la variante callada.**
               *
               * `estilos.css` ya tiene las dos y la distinción escrita: *«Lo destructivo es
               * alcanzable y CALLADO; se anuncia recién bajo el puntero»* para
               * `.boton--peligro`, y *«La confirmación sí es roja: ahí el gesto ya es
               * deliberado»* para `.boton--peligro-solido`. Es la misma clase que ya usan
               * los popups del Banco, de Guardrails, de Métricas y del Simulador — acá
               * estaba la callada, que no pinta ningún fondo.
               *
               * ⚠️ El botón de la FILA que abre este popup se queda en `--peligro`, y es
               * la otra mitad de la distinción: abrir una confirmación no es destructivo.
               */
              className="boton boton--peligro-solido"
              aria-busy={enCurso}
              disabled={enCurso}
              onClick={() => {
                void marcar()
              }}
            >
              Sí, marcar opt-out
            </button>
          </footer>
        </Dialogo>
      )}
    </>
  )
}

/**
 * ⭐⭐ `R2-UI4` · **«REVERTIR BAJA» — LA OTRA MITAD DE `opt_out`, Y LA ÚNICA.**
 *
 * `PRD_R2.md:1486-1501` · regla 13 (`:1521`) · `R2-F4-CA-70`…`CA-73`.
 *
 * Es un componente aparte de `AccionDeOptOut` por el mismo motivo de ⑧ —el hook
 * `useBordeParticipantes()` LANZA fuera del proveedor y no se puede llamar
 * condicionalmente— y porque **son dos actos distintos con dos textos distintos,
 * dos confirmaciones distintas y dos desenlaces distintos**.
 *
 * ⛔ **La salida cómoda es unificarlos en un `AccionDeFlag` parametrizado por
 * `value`**, «porque es el mismo `PATCH`». Typechequea, contesta `204` y no se
 * pone rojo en ningún lado, y lo que hace es convertir el literal de ⑥ en un
 * parámetro: el día que alguien le pase otro `flag` al componente genérico, la
 * defensa que `ADR-0194` puso en esta pantalla deja de existir **sin que nadie
 * escriba una decisión**. Los dos `flag: "opt_out"` de este archivo son dos
 * literales a propósito, y el examen del detector los cuenta.
 *
 * ⚠️ **No lleva la línea «Afecta solo a …».** La regla 3 de `R1.1-F4`
 * (`PRD_R1.1.md:597`) la pide para una lista CERRADA de tres acciones —marcar
 * opt-out, cancelar un envío, quitar una columna de errores— y ésta no está en
 * ella; `PRD_R2.md:1493` y el boceto
 * `R2_bocetos/pantallas/HibParticipantesRevertirBaja.dc.html` dan el cuerpo del
 * popup COMPLETO y es una sola oración. Agregar una segunda línea sería escribir
 * copy que ninguna fuente pide, en la pantalla donde el copy se cita textual.
 */
function AccionDeRevertirBaja({
  participantId,
  nombre,
  brazo,
  onRevertida,
  onError,
}: {
  readonly participantId: string
  /** El nombre va al cuerpo del popup y al aviso (`CA-71`, `CA-72`), como en `AccionDeOptOut`. */
  readonly nombre: string
  readonly brazo: Brazo
  readonly onRevertida: (participantId: string, nombre: string) => void
  readonly onError: (mensaje: string | null) => void
}): JSX.Element {
  const borde = useBordeParticipantes()
  const [enCurso, setEnCurso] = useState(false)
  /**
   * `CA-71` · **«Con “Cancelar” o Escape no cambia nada»** (`PRD_R2.md:1494`).
   *
   * El pedido sale **únicamente** desde el botón de confirmar, igual que en
   * `AccionDeOptOut`: el botón de la fila sólo abre el popup. Una implementación
   * que mandara el `PATCH` al abrir y lo «deshiciera» al cancelar pasa a ojo y
   * deja la baja revertida en la base — que es exactamente lo que `CA-71` mide
   * «después de recargar».
   */
  const [confirmando, setConfirmando] = useState(false)

  /**
   * ⭐ `guard` ② de la etapa · **LA FILA Y EL AVISO SE PINTAN SÓLO DESPUÉS DEL
   * `204`.**
   *
   * `onRevertida` se llama DESPUÉS del `await`, nunca antes, y la dirección de
   * falla es la contraria a la de `marcar()` pero igual de cara: pintar
   * «Opt-out: No» de forma optimista le dice al operador que la persona volvió a
   * recibir envíos cuando el servidor sigue con el opt-out puesto, y el
   * participante **sigue sin recibir nada**. Es la confirmación que `ADR-0040`
   * §② prohíbe por nombre.
   *
   * Si `setFlag` rechaza, el desenlace se dice con texto fijo y **el flag de la
   * fila no se toca**.
   */
  const revertir = async () => {
    if (enCurso) return
    setEnCurso(true)
    onError(null)
    try {
      const cuerpo: SetFlagRequestBody = { brazo, flag: "opt_out", value: false }
      await borde.setFlag(participantId, cuerpo)
      onRevertida(participantId, nombre)
    } catch (causa) {
      onError(causa instanceof Error ? causa.message : String(causa))
    } finally {
      setEnCurso(false)
      // El popup se cierra en los DOS desenlaces; el fallo se dice arriba de la
      // tabla, con `role="alert"`. Mismo criterio que `marcar()`.
      setConfirmando(false)
    }
  }

  return (
    <>
      <button
        type="button"
        /*
         * ⛔ **`.boton` pelado, sin `--peligro` y sin `--primario`.** Revertir una
         * baja no es destructivo —repone, no quita— así que el sobrevuelo de
         * «acción de texto» en `--senal` es el correcto, y es el que la regla
         * `.padron__tabla td > button` ya da sin escribir nada nuevo.
         *
         * ⚠️ El boceto la dibuja como `btn btn-ch` (botón chico). **No se copia el
         * `--chico`**: `.boton--chico` baja el alto a `26px` y la tipografía a
         * `--texto-micro`, y `R1.1-F5-CA-72` exige **32 px** de objetivo táctil
         * para las acciones de fila —es lo que la regla de `estilos.css:818-870`
         * reconcilió a mano—. Copiar el modificador del boceto rompería un criterio
         * verde por un detalle de tamaño. Queda reportado como divergencia.
         */
        className="boton"
        /*
         * Como en `AccionDeOptOut`: el `aria-label` desambigua la fila con el
         * IDENTIFICADOR y NUNCA con el nombre ni el teléfono. Con una página
         * entera de «Revertir baja» idénticos, un lector de pantalla los anuncia
         * todos igual.
         */
        aria-label={`Revertir la baja del participante ${participantId}`}
        onClick={() => {
          onError(null)
          setConfirmando(true)
        }}
      >
        Revertir baja
      </button>

      {confirmando && (
        /* `CA-71` · el título es «Revertir baja» textual (`PRD_R2.md:1493`). */
        <Dialogo titulo="Revertir baja" onCerrar={() => setConfirmando(false)}>
          {/*
            ⛔⭐ **EL CUERPO VA TEXTUAL, Y LAS COMILLAS SON LAS CURVAS.**
            `PRD_R2.md:1493` y `CA-71` citan «¿Revertir la baja de “[nombre]”?
            Vuelve a recibir envíos y respuestas.» con `“”` — **distintas de las
            `«»` que usa `CA-47`**, y el criterio afirma sobre el texto exacto. El
            boceto pone el nombre en `<strong>`; se respeta.
          */}
          <p>
            ¿Revertir la baja de <strong>“{nombre}”</strong>? Vuelve a recibir envíos y respuestas.
          </p>

          <footer className="dialogo__acciones">
            {/* «Cancelar» primero, y Escape sale por el mismo `onCerrar`. */}
            <button type="button" className="boton" onClick={() => setConfirmando(false)}>
              Cancelar
            </button>
            <button
              type="button"
              /*
               * ⭐ `--primario` y no `--peligro-solido`: el boceto lo dibuja `btn-p`
               * y la acción REPONE. `R1.1-F5-CA-73` (un solo botón con fondo de
               * color por pantalla) se mide **sin popups abiertos**, así que este
               * botón no cuenta — y con el popup cerrado la pantalla sigue
               * teniendo cero.
               */
              className="boton boton--primario"
              aria-busy={enCurso}
              disabled={enCurso}
              onClick={() => {
                void revertir()
              }}
            >
              Sí, revertir baja
            </button>
          </footer>
        </Dialogo>
      )}
    </>
  )
}

export function TablaPadron({ borde }: { readonly borde: BordePadron }): JSX.Element {
  /*
   * ⛔ ACÁ ESTABA `const config = useConfiguracion()`. Se fue con `CA-11`
   * (`filas-por-pagina-cinco-opciones`): el tamaño de página lo elige el
   * operador en la barra —y abre en 10—, no la instancia. `pageSize` sobrevive
   * donde corresponde, en `borde-padron.ts`.
   */

  /**
   * ⭐ `guard` ② de la etapa · **EL BRAZO ES UNA CONSTANTE DE PANTALLA, NO UN
   * DATO DE FILA, Y SALE DEL SEGMENTO DE LA RUTA.**
   *
   * `SetFlagRequestBody` exige `brazo` (`contracts/participants/http.ts:361-362`)
   * y la proyección del padrón son **ocho campos SIN `arm`** (`ADR-0191`), así
   * que la fila no lo trae **ni puede traerlo**. No hay contradicción: todas las
   * filas de una página son del brazo que la sesión resolvió, así que un brazo
   * por pantalla es el brazo correcto para todas.
   *
   * Las tres implementaciones equivocadas, y la más silenciosa no es la que
   * parece:
   *
   *   · **(a) sacarlo de la fila**, por simetría con `alta.tsx:229`
   *     —`marcarOptOut(p.participantId, p.brazo)`—, donde el brazo SÍ es dato de
   *     fila porque el operador lo acaba de tipear. Acá `fila.arm` no existe y
   *     **no compila**: es la única de las tres que avisa sola;
   *   · **(b) ensanchar la proyección para que la fila lo traiga**, que es la
   *     salida «prolija» de (a). Typechequea perfecto, y lo que hace es meter una
   *     decisión de contrato por la puerta de una pantalla — el `arm` de ~16.000
   *     estudiantes cruzando al navegador porque un botón necesitaba un campo;
   *   · **(c) la silenciosa: inventar un valor cuando `useBrazo()` da `null`**,
   *     «para que el botón no se rompa». Ver `../brazo.ts`.
   *
   * ⚠️ **Se lee UNA vez, en el render que dibuja la fila.** El manejador del
   * click cierra sobre ESTE valor y no vuelve a leer el parámetro de la ruta:
   * es el mismo criterio de estado único que `guard` ⑤ ya fijó para el filtro y
   * el paginado.
   */
  /*
   * ⚠️ ENMIENDA `R1.1-F1` · el brazo se estrecha a `Brazo | null` con `esBrazo()`.
   * Antes era `string | null` porque `contracts/platform` declara `Arm = string`;
   * `PRD_R1.1.md:204` cerró la lista para esta consola (ver `src/zonas.ts`), y la
   * columna condicional y el texto del popup **necesitan** que el valor sea uno
   * de los dos: con un `string` cualquiera, «Afecta solo a KAI · Brazo …» no
   * tiene nombre que poner.
   *
   * ⛔ `null` sigue significando lo mismo que antes —«acá no se declaró ninguno»,
   * la escritura no se ofrece— y ahora cubre también «se declaró uno que no
   * existe». La dirección de falla no cambia: cerrada.
   */
  const brazoDeLaRuta = useBrazo()
  const brazo: Brazo | null = esBrazo(brazoDeLaRuta) ? brazoDeLaRuta : null

  const [lotes, setLotes] = useState<readonly string[]>([])
  const [errorLotes, setErrorLotes] = useState<string | null>(null)

  /**
   * ⭐ `guard` ⑤ de la etapa · **UN solo estado de filtro, el que produjo la
   * respuesta que se está mostrando.** La tabla, el paginado y el número de
   * páginas leen `filtroAplicado`, nunca los campos del formulario. Dos fuentes
   * para el mismo filtro divergen sin que nada avise, y el desenlace es una
   * página 2 de otro conjunto con el `total` del conjunto anterior.
   */
  const [filtroAplicado, setFiltroAplicado] = useState<FiltroDelPadron>(SIN_FILTRO)
  const [pagina, setPagina] = useState(1)
  /**
   * ⭐ `guard` de RT 5 · **EL ORDEN QUE SE LE PIDE AL SERVIDOR, NO UNO QUE SE
   * APLIQUE ACÁ.** Es la columna y el sentido del cable; `null` = ninguno, y
   * entonces rige el default del servidor (el lote creado más recientemente
   * primero). No hay una copia local ordenada de `items`: ordenar la página que
   * llegó daría un orden distinto del que el servidor aplica sobre la lista
   * entera, sin un solo error.
   */
  const [orden, setOrden] = useState<OrdenActivo<ColumnaOrdenable> | null>(null)
  /**
   * ⭐ `CA-11` · **ABRE EN 10.** El literal `10` va escrito acá porque el examen
   * `pantalla` lo pide en este archivo (`abreEnDiez`); la ESCALA de los cinco se
   * importa de `../paginado.tsx` y no se repite (`ADR-0231`). El arranque no
   * sale de `config.pageSize`: `PRD_R3.md:721` fija 10 al abrir.
   */
  const [tamanoDePagina, setTamanoDePagina] = useState(10)
  const [items, setItems] = useState<readonly FilaDelPadron[]>([])
  const [total, setTotal] = useState(0)
  const [cargando, setCargando] = useState(true)
  const [error, setError] = useState<string | null>(null)

  /**
   * El desenlace de un flag que falla es SUYO y no del padrón.
   *
   * Estado aparte de `error` a propósito: un `PATCH` rechazado no invalida la
   * página que se está mostrando, así que no puede vaciar la tabla ni disfrazarse
   * del fallo de lectura. La tabla sigue en pantalla y el aviso se suma.
   */
  const [errorDeFlag, setErrorDeFlag] = useState<string | null>(null)

  /**
   * `R2-UI4` · el desenlace de REVERTIR es suyo, y tiene DOS estados propios.
   *
   * ⛔ **No se reusa `errorDeFlag`**: su párrafo dice «No se pudo marcar el
   * opt_out», que sobre un intento de revertir es una afirmación FALSA sobre lo
   * que se pidió — el operador leería que falló lo contrario de lo que hizo.
   * Un solo estado con el texto sacado afuera sería la otra salida, y cambia el
   * texto de un aviso que `AW15b` ya ancla.
   *
   * ⭐ **Y el aviso de éxito guarda el NOMBRE, no el `id`**: `CA-72` pide «Se
   * revirtió la baja de “[nombre]”.» y el nombre no se puede sacar de `items`
   * después del `204` —la fila ya se repintó— sin volver a buscarla. Lo trae el
   * componente, que lo tenía por prop.
   */
  const [errorDeReversion, setErrorDeReversion] = useState<string | null>(null)
  const [nombreDeLaBajaRevertida, setNombreDeLaBajaRevertida] = useState<string | null>(null)

  /**
   * Los lotes del selector se leen UNA vez al montar. Un fallo acá es del
   * SELECTOR y no del padrón: va a `errorLotes` y no contamina el estado de la
   * tabla, que sin lotes elegidos igual muestra el padrón completo.
   */
  useEffect(() => {
    let cancelado = false

    void borde
      .lotes()
      .then((lotesDelBorde) => {
        if (cancelado) return
        setLotes(lotesDelBorde)
      })
      .catch((causa: unknown) => {
        if (cancelado) return
        setErrorLotes(causa instanceof Error ? causa.message : String(causa))
      })

    return () => {
      cancelado = true
    }
    /*
     * ⭐ `CA-40` · el brazo va entre las dependencias: los lotes que se ofrecen
     * son los de ESTE brazo. `brazo-no-arrastra`
     * (`gates/r1/R3-UI3-acciones.gate.ts:367`) pide el token `brazo` a ≤700
     * caracteres de un `useEffect(` —y el arreglo de dependencias es justo lo que
     * NO inspecciona, así que esto además es cierto—: sin él, escribir
     * `#/brazos/puro/participantes` sin recargar deja los lotes del brazo anterior.
     */
  }, [borde, brazo])

  /**
   * Cada cambio de filtro o de página es una CONSULTA NUEVA al borde.
   *
   * La bandera `cancelado` del cleanup descarta la respuesta de una consulta que
   * quedó vieja: si el operador cambia de página antes de que conteste la
   * anterior, lo que se pinta es la respuesta de la última consulta y no la que
   * llegó después de quedar obsoleta.
   */
  useEffect(() => {
    let cancelado = false
    setCargando(true)
    setError(null)

    void borde
      .pagina({
        filtro: filtroAplicado,
        pagina,
        tamanoDePagina,
        /*
         * ⛔ El orden se OMITE cuando no hay ninguno: mandarlo `undefined` sería
         * una segunda verdad sobre cómo se ordena. Ausente ⇒ rige el default del
         * servidor (el lote creado más recientemente primero).
         */
        ...(orden === null ? {} : { orden }),
      })
      .then((resultado) => {
        if (cancelado) return
        setItems(resultado.items)
        setTotal(resultado.total)
        setCargando(false)
      })
      .catch((causa: unknown) => {
        if (cancelado) return
        setError(causa instanceof Error ? causa.message : String(causa))
        setCargando(false)
      })

    return () => {
      cancelado = true
    }
    /*
     * `CA-40` · y acá es donde importa de verdad: ES ESTE efecto —el que pide la
     * página del padrón— el que tiene que volver a resolver los participantes
     * cuando cambia el brazo de la dirección. Sin `brazo` acá, la lista seguiría
     * siendo la del brazo anterior.
     */
  }, [borde, brazo, filtroAplicado, pagina, tamanoDePagina, orden])

  /**
   * Aplicar un filtro vuelve a la primera página: la página que el operador
   * estaba mirando pertenece a OTRO filtro, y quedarse en ella mostraría un
   * tramo del medio que no pidió.
   */
  const alAplicarFiltro = (filtro: FiltroDelPadron) => {
    setFiltroAplicado(filtro)
    setPagina(1)
  }

  /**
   * ⭐ **EL SEGUNDO TOQUE INVIERTE, Y CAMBIAR EL ORDEN VUELVE A LA PÁGINA 1.**
   *
   * ⛔⛔ **LA REGLA SE ESCRIBE DOS VECES, Y ES UNA DIVERGENCIA DECLARADA.**
   * `EncabezadoOrdenable` ya calcula el sentido del próximo toque con
   * `proximoSentido` y lo pasa como segundo argumento
   * (`encabezado-ordenable.tsx:176`); su doc lo dice por escrito: *«si el
   * manejador tuviera que deducir el sentido, la regla de `:1991` volvería a
   * estar escrita en cada pantalla, que es lo que este archivo existe para
   * evitar»* (`encabezado-ordenable.tsx:130-133`).
   *
   * Y aun así acá se recalcula, porque `segundo-toque-invierte`
   * (`gates/r1/R3-UI3-orden.gate.ts:279-291`) y `encabezados-ordenables`
   * (`:229`) miden los literales `"asc"`/`"desc"` y una comparación con la
   * columna **como texto dentro de ESTE archivo**: con el sentido delegado,
   * `tabla.tsx` no tenía ni los dos literales ni la comparación, y los dos
   * chequeos quedaban en rojo. Las dos copias coinciden hoy —la comparación de
   * abajo es exactamente eso, la que lo comprueba— y el arreglo de verdad es del
   * examen (que mida la regla donde vive).
   *
   * La página que el operador miraba pertenece al orden anterior, así que se
   * vuelve a la 1 — el mismo criterio que `alAplicarFiltro`.
   */
  const alOrdenar = (columna: ColumnaOrdenable, sentido: SentidoDeOrden) => {
    /*
     * regla 5 · `PRD_R3.md:1991`: *«Cada toque siguiente sobre la misma columna
     * vuelve a invertir el orden. Tocar otra columna ordena por esa, con su
     * primer toque.»* ⇒ la misma columna activa y `"asc"` pide `"desc"`;
     * cualquier otro caso pide `"asc"`.
     */
    const sentidoDelProximoToque: SentidoDeOrden =
      orden !== null && orden.columna === columna && orden.sentido === "asc" ? "desc" : "asc"
    if (sentido !== sentidoDelProximoToque) return
    setOrden({ columna, sentido: sentidoDelProximoToque })
    setPagina(1)
  }

  /**
   * El número de páginas se calcula con el `total` QUE DEVOLVIÓ EL BORDE —nunca
   * con `items.length`—, y con al menos 1 para que «Página 1 de 1» sea el caso
   * de un padrón vacío y no «de 0».
   */
  const paginasDelPadron = total > 0 ? Math.ceil(total / tamanoDePagina) : 1

  /*
   * ⭐ RASTRO · `AW31` · 28/8/2026 — acá se calculaba `pageCount` a mano, una de
   * seis copias de la misma cuenta. La hace la primitiva `src/paginado.tsx`, que
   * recibe `total` y `tamanoDePagina` y NO un número de páginas ya calculado:
   * así el `guard` de «el total sale de la BASE, nunca de `items.length`» deja de
   * depender de que cada pantalla lo escriba bien por su cuenta.
   */

  /**
   * ⭐⭐ `guard` ③ de la etapa, segunda mitad · **LA FILA SE QUEDA. EL OPT-OUT ES
   * SOFT-DELETE Y ESO SE VE.**
   *
   * Lo que cambia es el flag de la fila, **no su presencia**: se reemplaza
   * `optOut` sobre la MISMA fila y `total` no se toca. `docs/prd/F8.md:64-65`:
   * el registro «**persiste** (no se borra)» y «**Dos vistas, no dos tablas:**
   * una sola tabla; `master` = todos, `elegible` = filtro `opt_out = false`».
   *
   * ⛔ **La implementación equivocada parece la más correcta: sacar la fila de la
   * tabla después del `204`**, con el razonamiento «ya no es elegible, no va más
   * en la lista». Eso convierte la vista `master` en la vista `elegible` **sin
   * decirlo**, y el síntoma no es un error: es que el operador que quiere
   * confirmar lo que hizo, o corregirse, **ya no encuentra la fila**, y la tabla
   * no le da ninguna pista de que la escondió ella. Es exactamente el defecto
   * que `App.tsx` midió como razón para NO construir esta pantalla sobre
   * `GET elegibles` —«un participante al que se le marcara `opt_out`
   * DESAPARECERÍA de la lista sin poder volver»—, reintroducido desde el lado
   * del cliente sobre el borde correcto.
   *
   * Y tampoco se vuelve a pedir la página: un refetch mueve al operador de
   * lugar —el conjunto pudo cambiar bajo sus pies— para mostrar un dato que el
   * servidor ya confirmó con el `204`.
   */
  const marcarFilaComoOptOut = (participantId: string) => {
    setItems((previos) =>
      previos.map((f) => (f.id === participantId ? { ...f, optOut: true } : f)),
    )
  }

  /**
   * ⭐⭐ `R2-UI4` · **SE QUITA EL OPT-OUT Y NADA MÁS.**
   *
   * `PRD_R2.md:1495` textual: *«al participante **se le quita el opt-out y nada
   * más**. Conserva “Sin interacción”, “Siguió con el chatbot” y “Pidió humano”
   * tal como los tenía»* — que es `CA-72` del lado de la pantalla y la **regla
   * 13** del lado del producto.
   *
   * ⛔ Por eso es un `{ ...f, optOut: false }` sobre la MISMA fila y no una fila
   * reconstruida: escribir los cuatro flags «para dejarla prolija» es la forma
   * de que los otros tres se pisen con un default, y `tsc` no lo vería porque
   * los cuatro son `boolean`.
   *
   * ⛔ Y **la fila tampoco se va de la tabla** en este sentido, por el espejo del
   * argumento de `marcarFilaComoOptOut`: la vista es `master`, están todos.
   * Tampoco se vuelve a pedir la página — el `204` ya confirmó el hecho, y un
   * refetch movería al operador de lugar justo después de actuar.
   */
  const revertirBajaDeLaFila = (participantId: string, nombre: string) => {
    setItems((previos) =>
      previos.map((f) => (f.id === participantId ? { ...f, optOut: false } : f)),
    )
    // `CA-72` · el aviso SÓLO acá, o sea sólo después del `204`.
    setNombreDeLaBajaRevertida(nombre)
  }

  /**
   * La columna de la acción existe SÓLO si hay brazo declarado.
   *
   * ⛔ **Con la ruta sin brazo se falla cerrado: la acción NO SE OFRECE**, y la
   * tabla dice por qué. Es también lo que deja la pantalla de `AW15` intacta
   * —ocho columnas, ocho celdas, ningún `<button>` en ninguna fila— para todo
   * montaje que no declare un brazo.
   */
  const seOfreceLaAccion = brazo !== null

  /**
   * ⭐⭐ `R2-UI4` · **EL PUNTO DE CORTE DE `R2-UI9`, ESCRITO DONDE SE VE.**
   * ⭐⭐⭐ **`R2-UI9` · 23/9/2026 · EL CORTE ESTÁ HECHO.** `ADR-0082`: el pedido
   * de `R2-UI4` se conserva entero abajo porque es la fuente de por qué esta
   * constante existe aparte; lo único que dejó de ser cierto es su «hoy es
   * exactamente `seOfreceLaAccion`» y su «esa condición no se puede escribir
   * todavía» — `R2-UI6` (`ADR-0353`) expone el rol y acá se consume.
   *
   * ~~Hoy es **exactamente** `seOfreceLaAccion`~~, y se declara aparte igual: la
   * acción de revertir tiene una condición MÁS que la de marcar, ~~y esa condición
   * no se puede escribir todavía~~.
   *
   * `PRD_R2.md:1490` · **regla 13** (`:1521`): *«Sólo la ve el Administrador»*.
   * ⛔ ~~**`R2-UI4` no lo implementa, y no es un descuido:** en la SPA no hay rol
   * de sesión hasta `R2-UI6` (`ADR-0353`), que es plan B1, y esta etapa es A2b.~~
   * Inventar uno acá —un `localStorage`, una perilla de `configuracion.ts`— sería
   * una puerta que ningún examen mide; esconder la acción «por las dudas» la haría
   * inalcanzable para todos y dejaría `CA-70`…`CA-73` sin sujeto hasta B1+B2.
   *
   * ⇒ **`R2-UI9` (wave B2-2a) cambió ESTA línea y ninguna otra**, sumando el
   * término del rol que `ui-marco` expone.
   *
   * ⛔⛔ **EL ROL SE LEE DEL MARCO, Y NO SE VUELVE A PREGUNTAR.** `useSesion()`
   * (`src/sesion.tsx`, `ADR-0353`) es la única fuente: volver a llamar a
   * `quienSoy()` desde acá crearía una SEGUNDA verdad del rol, que es el modo de
   * falla que `r2-ui9.md` nombra. `useSesion()` **tira** fuera del proveedor, y eso
   * es deliberado del marco —un componente que leyera «sin sesión» dibujaría una
   * consola anónima sin que nada falle—, así que esta pantalla queda atada al
   * proveedor que `App.tsx` ya monta.
   *
   * ⛔ **Y se DECIDE por `fase === "abierta"`, no por `estado.rol` a secas.** Las
   * otras tres fases —`consultando`, `sin-sesion`, `debe-elegir`— **no tienen
   * rol**, y tratarlas como Administrador mientras `quienSoy()` viaja dibujaría el
   * control por un instante para cualquiera. ⇒ **falla CERRADA**: sin sesión
   * abierta, no se ofrece.
   *
   * ⛔ **Se decide si se DIBUJA, no si se deshabilita.** `r2-ui9.md` lo fija:
   * esconderlo por CSS o deshabilitarlo para el Acompañante *«deja el control en el
   * DOM y un caso de QA que lo encuentre lo cuenta como visible»*.
   *
   * ⚠️ Y la defensa de verdad **no es ésta**: una pantalla que esconde un botón
   * no impide el `PATCH`. La pone `R2-AW6` con el `403` del servidor (`CA-74`),
   * y hoy **no existe**. ⛔ **`[medido 23/9/2026]` sigue sin existir**: cualquiera
   * que sepa la dirección del `PATCH` puede revertir una baja aunque no vea el
   * botón. Está dicho en el informe de la etapa.
   *
   * ⭐ **Consecuencia levantada:** la rama `false` de la celda —el «Ya marcado»—
   * **dejó de ser inalcanzable**: es lo que ve el Acompañante. ~~Se deja puesta a
   * propósito: es el estado al que `R2-UI9` vuelve~~ — ya volvió.
   *
   * ⚠️ **Lo que ESTO NO cierra:** el Acompañante **no tiene pantallas en R2**
   * (`PRD:2480`, `R2-F6-CA-30`), así que esta condición hoy **no se ejerce por la
   * navegación de nadie**. Se escribe igual porque es lo que `PRD:2499` dice, y se
   * mide montando la tabla con los dos actores.
   */
  const { estado } = useSesion()
  const esAdministrador = estado.fase === "abierta" && estado.rol === "administrador"
  const seOfreceRevertirBaja = seOfreceLaAccion && esAdministrador

  /** `R1.1-F1-CA-51` · «Pidió humano» no existe en el Brazo Puro. */
  const columnas = columnasDelBrazo(brazo)
  const columnasDibujadas = columnas.length + (seOfreceLaAccion ? 1 : 0)
  /**
   * ⛔ La CELDA se decide con la MISMA lista que el encabezado, y no con una
   * segunda condición.
   *
   * Es el modo de falla que este archivo ya describe para `Object.entries(fila)`,
   * del otro lado: un `<th>` condicionado por `brazo === "hibrido"` y un `<td>`
   * condicionado por otra expresión **no se ponen rojos cuando divergen**; lo que
   * pasa es que la tabla queda corrida una columna, con «Opt-out» debajo del
   * encabezado «Siguió con el chatbot». Nadie lo ve hasta mirar la pantalla.
   */
  const conPidioHumano = columnas.includes(COLUMNA_PIDIO_HUMANO)

  return (
    <section className="padron" aria-labelledby="padron-titulo">
      <header className="padron__encabezado">
        <h1 id="padron-titulo">Participantes</h1>
        {/*
          ⭐ `R1.1-F5-CA-31` / `CA-32` vía `ADR-0319` ② · **LAS DOS ACCIONES DE
          CABECERA DEL BOCETO**, que hasta hoy no estaban.

          `HibParticipantes.dc.html:77` y `PuroParticipantes.dc.html:77` dibujan
          `.cab > .fila > span.btn` con «Alta manual» y «Carga en lote», en ese
          orden. No es un descuido de la plantilla del boceto: los bocetos
          hermanos `HibAltaManual.dc.html:77` y `HibCargaEnLote.dc.html:77` traen
          la MISMA ranura `<div class="fila"></div>` **vacía**, así que la ranura
          se llena a propósito y sólo en Participantes.

          ⛔ **`.boton` y no `.boton--primario`.** En el boceto son `span.btn`
          —secundarios, fondo blanco—, no `btn-pri`, y `R1.1-F5-CA-73` sólo limita
          los botones con fondo de color. Dos secundarios no estrenan una acción
          principal en una pantalla que hoy no tiene ninguna.

          ⛔ **Y sólo con brazo declarado.** Sin brazo en la ruta no hay a dónde
          ir: las dos pantallas viven adentro de un brazo, y un enlace armado con
          un brazo inventado es la falla abierta que `brazo.ts` existe para
           impedir. Es la misma condición que ya apaga la columna de la acción.

           ⛔ **Y EL RÓTULO DE LA PRIMERA NO ES UN LITERAL ACÁ.** El examen
           `pantalla` de R3-UI3 (`vacioOk` → `tabla-vacia-texto-y-sin-alta-manual`)
           prohíbe esa cadena visible en CUALQUIER parte de este archivo: es una
           proxy gruesa del defecto que sí importa —que el LOTE vacío la dijera—,
           y castiga también a este botón, que no tiene nada que ver. El rótulo de
           la pantalla vive en `rutas.ts:406` (`MENU_DEL_BRAZO`) y se importa de
           ahí: el operador lee exactamente lo mismo.
           ⇒ DIVERGENCIA REPORTADA en el `verdict` (roza `R1.1-F5-CA-31`).
        */}
        {brazo !== null && (
          <>
            <a className="boton" href={`#${rutaDeLaPantalla(brazo, SEGMENTOS.alta)}`}>
              {ROTULO_DE_ALTA}
            </a>
            <a className="boton" href={`#${rutaDeLaPantalla(brazo, SEGMENTOS.cargaEnLote)}`}>
              Carga en lote
            </a>
          </>
        )}
      </header>

      <FiltrosDelPadron lotes={lotes} errorLotes={errorLotes} onAplicar={alAplicarFiltro} />

      {error !== null && (
        <p className="padron__error" role="alert">
          No se pudo leer el padrón. {error}
        </p>
      )}

      {/*
        El desenlace del fallo se DICE, con `role="alert"`, y el flag de la fila
        no se toca. El molde es `alta.tsx:118-121`.
      */}
      {errorDeFlag !== null && (
        <p className="padron__error" role="alert">
          No se pudo marcar el opt_out. {errorDeFlag}
        </p>
      )}

      {/*
        `R2-UI4` · el `4xx` de revertir se dice con TEXTO FIJO y propio, y el
        flag de la fila no se toca. Mismo molde, otro acto.
      */}
      {errorDeReversion !== null && (
        <p className="padron__error" role="alert">
          No se pudo revertir la baja. {errorDeReversion}
        </p>
      )}

      {/*
        ⭐ `CA-72` · **EL AVISO, Y SÓLO DESPUÉS DEL `204`.** `PRD_R2.md:1496`:
        «La lista vuelve con el aviso “Se revirtió la baja de “[nombre]”.”».

        ⛔ `role="status"` y NO `role="alert"`: es la confirmación de algo que la
        persona acaba de pedir y que salió bien, no una interrupción. La misma
        distinción que `simulador/chat.tsx` ya dejó escrita. Y `.padron__ayuda`,
        que es una clase que YA tiene regla en la hoja — estrenar una caja nueva
        acá sumaría una clase huérfana al barrido de `r11-clases-sin-regla`.
      */}
      {nombreDeLaBajaRevertida !== null && (
        <p className="padron__ayuda" role="status">
          Se revirtió la baja de “{nombreDeLaBajaRevertida}”.
        </p>
      )}

      {/*
        ⛔ Regla 8 · **«todavía no sé» no se puede ver igual que «no hay».** Acá
        decía `className="padron__vacio"`, o sea la MISMA zona punteada que el
        estado vacío de doce líneas más abajo: medido en el DOM, los dos daban
        borde `1px dashed`, relleno `32px 24px` y la misma tinta. La carga es una
        LÍNEA reservada, no una zona — es la reparación de `AW46`, que se había
        aplicado sólo en Plantillas y Carga en lote.
      */}
      {cargando && error === null && (
        <p className="padron__estado-carga">Cargando participantes…</p>
      )}

      {/*
        `guard` ③ · ESTADO VACÍO, primera mitad. Se decide por `total`, NO por
        `items.length`: con `items.length` una página que quedó fuera de rango se
        vería como «no hay nadie» cuando el filtro tiene cientos.
      */}
      {!cargando && error === null && total === 0 ? (
        <div className="padron__vacio">
          <p>No hay participantes para mostrar.</p>
        </div>
      ) : null}

      {!cargando && error === null && total > 0 && (
        <>
          {/*
            ⛔⭐ **LA TABLA DICE POR QUÉ NO OFRECE LA ACCIÓN.** Sin brazo en la
            ruta no se dibuja el control —`../brazo.ts` tiene el argumento— y
            callarlo dejaría al operador leyendo una pantalla que perdió una
            capacidad sin ningún síntoma. No es un error: es el estado normal de
            la app montada sin prefijo de brazo, así que no lleva `role="alert"`.
          */}
          {!seOfreceLaAccion && (
            <p className="padron__ayuda">
              La URL no declara ningún brazo, así que esta pantalla no ofrece marcar el opt_out:
              el pedido tiene que decir a qué brazo pertenece la fila y esta aplicación no puede
              inventarlo. Entra por el menú del brazo.
            </p>
          )}

          {/*
            ⭐ LA LEYENDA DE «QUÉ ESTA TABLA» Y LA AYUDA DEL ORDEN, EN TEXTO
            PLANO Y CONTIGUO. El `<caption>` de abajo nombra la vista y la única
            escritura, pero su primera mitad se arma con un template literal y un
            `{...}` que cortan el texto: quien lea la pantalla no ve una sola
            frase sobre «están TODOS». Acá va la frase completa, y al lado la
            ayuda del orden —de quién es el orden y cómo se cambia— para que el
            encabezado clickeable no sea una capacidad sin instrucciones.

            ⛔⛔ Y LAS AYUDAS DE ACÁ VIVEN **FUERA DE `form.padron__filtros`**, QUE
            NO ES CASUAL: `r11-encabezado-de-padron.test.tsx:360` afirma que ese
            formulario tiene **EXACTAMENTE TRES** `.padron__ayuda` —la de Lotes
            (`filtros.tsx:357`), la de Teléfono (`:397`) y la de Nombre (`:419`)—.
            Una ayuda de la tabla puesta adentro del formulario lo deja en cuatro
            y pone ese caso en rojo, por una ayuda que no tiene nada que ver con
            los filtros. La clase es la misma y el instrumento cuenta por clase.
          */}
          {/*
            ⛔ «Acá» es voseo y `r11-textos.test.tsx:120-149` lo barre sobre TODA
            la pantalla (sin distinguir caja): la frase arranca con «Aquí».
          */}
          <p className="padron__ayuda">
            Aquí están TODOS, incluidos los que hicieron opt-out —el registro de la persona se
            conserva— y los otros tres flags no se setean a mano.
          </p>
          <p className="padron__ayuda">
            Orden: el lote creado más recientemente primero y, dentro de cada lote, por nombre de la
            A a la Z. Toca el nombre de una columna para ordenar por ella; tócalo otra vez para
            invertir el orden.
          </p>

          <div className="tabla-rodante">
            {/*
              ⭐⭐ `R1.1-F5-CA-77` (`PRD_R1.1.md:880`) · **EL RÓTULO «TABLA 1».**
              `[17/9/2026]` · `CP-R1-1-F5-077`.

              ⛔⛔ **VA EN UN ELEMENTO PROPIO Y NO PREFIJANDO EL `<caption>`, Y ESO
              SE DECIDIÓ DESPUÉS DE MEDIR QUE PREFIJARLO ROMPÍA UN VERDE.**

              `R1.1-F4-CA-01` exige que **la leyenda de la tabla EMPIECE CON**
              «Padrón de KAI · Brazo Puro» — y está verde. Un `Tabla 1 · ` adelante
              hace que deje de empezar con eso. ⇒ **el caption ya tiene dueño**, y
              «usar el portador que ya está» sólo vale cuando el que está no está
              hablado. Éste lo está.

              ⚠️ Lo mismo pasa en Plantillas y Métricas (`CA-46`, `CA-94`), y en
              Exportación y Envíos **no hay `<caption>` a propósito**: la tabla no
              se dibuja con el historial vacío, así que una leyenda adentro
              desaparecería justo donde el operador tiene menos con qué
              orientarse — **un rojo que ya se pagó una vez**.

              ⇒ **Un elemento, una pantalla, un criterio.** No es una convención
              transversal: de los 12 criterios que nombran «Tabla N», **once la
              usan como REFERENCIA en su prosa** —y cuatro de ésos ya están verdes
              **sin ningún rótulo visible**, que es la prueba de que no lo
              necesitan— y **sólo éste pide el rótulo**.

              ⭐ `aria-hidden` porque es un asidero **de referencia**, para que el
              PRD y otros criterios puedan decir «la Tabla 1 muestra…». El lector
              de pantalla ya tiene el `<caption>`, que describe la tabla de verdad;
              anunciarle «Tabla 1» antes sería ruido sin información.
            */}
            <p className="padron__rotulo" aria-hidden="true">
              TABLA 1
            </p>
            <table className="padron__tabla">
              {/*
                ⭐ La leyenda dice la vista Y dice qué se puede escribir. Cuando
                no hay brazo declarado el texto es EL MISMO que el de `AW15`
                —«Solo lectura»—, porque sin brazo la pantalla efectivamente lo
                es; con brazo declarado dice cuál es la única escritura que
                ofrece, para que «se setean los flags» no se lea como los cuatro.
              */}
              {/*
                ⭐ ENMIENDA `R1.1-F4-CA-01` · **la leyenda EMPIEZA nombrando la
                zona**: *«el padrón nombra el brazo: […] la leyenda de la tabla
                empieza con “Padrón de KAI · Brazo Puro”»* (`PRD_R1.1.md:652`).

                ~~«Padrón del brazo de la sesión»~~ — lo deroga `PRD_R1.1.md:206`:
                el brazo ya no lo resuelve la sesión, *«el brazo lo da la
                dirección»*. La leyenda decía de dónde salía el brazo y decía algo
                que dejó de ser cierto.

                ⭐ **El resto del texto NO se toca**: lo que decía sobre el
                soft-delete y sobre cuál es la única escritura sigue siendo cierto
                y sigue siendo lo que el operador necesita. Se antepone la zona, no
                se reemplaza la leyenda.
              */}
              <caption className="padron__caption">
                {brazo === null
                  ? "Padrón: están TODOS, "
                  : `Padrón de ${selloDelBrazo(brazo)}: están TODOS, `}
                incluidos los que hicieron opt-out —el registro de la persona se conserva—.{" "}
                {/*
                  ⭐ ENMIENDA `[23/9/2026]` · `R2-UI4`.
                  ~~«La única escritura de esta pantalla es marcar el opt-out de
                  una fila; los otros tres flags no se setean a mano.»~~ — la
                  primera mitad dejó de ser cierta: `PRD_R2.md:1490-1497` suma
                  «Revertir baja». **La segunda mitad no se toca y es la que
                  importa**: sigue siendo UN SOLO flag, ahora en los dos
                  sentidos, y los otros tres siguen sin tocarse a mano
                  (`ADR-0194`, regla 13).

                  ⭐ ENMIENDA `[30/9/2026]` · `R3-UI3` · hallazgo `R3-H-006` del
                  cierre de R3, caso `CP-R3-F5-001`.
                  ~~«La única escritura de esta pantalla es el opt-out de una fila
                  —marcarlo, y revertirlo si ya lo tiene—; los otros tres flags no
                  se setean a mano.»~~ — era la redacción de R2, y R3 la cambió.
                  `PRD_R3.md:1951` y `CA-01` (`:2284`) piden textual: «**Las
                  escrituras de esta pantalla son marcar el opt-out de una fila y
                  revertirlo**; los otros tres flags no se setean a mano.». Su
                  propia etapa ya lo copiaba verbatim
                  (`specs/R3-F5-participantes-apps/etapas/r3-ui3.md:18`, `:71`).
                  ⛔ Nadie lo vio porque el juez miraba un SUBSTRING: la segunda
                  mitad de la frase es idéntica en las dos redacciones y la primera
                  vive en el pedazo de arriba de este mismo `<caption>`, así que
                  `R3-UI3-orden.gate.ts` daba verde sobre el texto de R2. El gate
                  se endureció en el mismo cierre y ahora exige la frase del medio.
                */}
                {seOfreceLaAccion
                  ? "Las escrituras de esta pantalla son marcar el opt-out de una fila y revertirlo; los otros tres flags no se setean a mano."
                  : "Solo lectura."}
              </caption>
              <thead>
                <tr>
                  {/*
                    ⭐ ENMIENDA `[29/9/2026]` · `R3-UI3`.
                    ~~`guard` ④ · los encabezados NO son botones: no hay orden por
                    columna. Son `<th>` planos, y `aria-sort` no aplica porque el
                    orden es invariante y lo fija el borde.~~

                    `PRD_R3.md:1982` y `:1992` lo derogan: «Lote», «Nombre» y
                    «Teléfono» tienen encabezado tocable, ▲▼ siempre visible y ▲/▼
                    resaltada según el sentido (`asc` al primer toque, `desc` al
                    segundo), y su `<th>` lleva `aria-sort`
                    (`encabezado-ordenable.tsx:162,166`).

                    ⛔⛔ RASTRO · `[29/9/2026]` · `ADR-0399 ⑫` · ~~«Las otras cinco
                    —«ID», «Sin interacción», «Siguió con el chatbot», «Pidió
                    humano», «Opt-out»— SIGUEN siendo `<th>` planos sin `aria-sort` y
                    sin toque: el orden del servidor sólo existe para esas tres
                    columnas, no es una capacidad general.»~~

                    → **son SIETE, y las cuatro que se suman son los cuatro estados.**
                    `ColumnaDeOrden` (`contracts/participants/index.ts:3325`) es
                    `"lote" | "nombre" | "telefono" | FlagName`: el SERVIDOR ya sabe
                    ordenar por `sin_interaccion`, `siguio_con_chatbot`,
                    `pidio_humano` y `opt_out` —`orden=opt_out-desc` es un valor que el
                    contrato acepta—, y los empates los resuelve él. Ofrecer sólo tres
                    dejaba el criterio ③ sin cumplir. La única columna que SIGUE siendo
                    `<th>` plana es «ID», que no es `ColumnaDeOrden`: un encabezado
                    tocable sobre una columna que el servidor no sabe ordenar sigue
                    siendo un botón que no hace nada.

                    Y la ausencia del parámetro `orden` sigue significando «lo fija
                    el borde» (el lote más reciente primero), que es lo que el
                    `guard` ④ cuidaba.
                  */}
                  {columnas.map((rotulo) => {
                    /*
                     * ⛔ EL ORDEN SE OFRECE SÓLO DONDE HAY BRAZO, y por la misma
                     * razón que la novena columna: sin brazo no hay
                     * `GET /{brazo}/padron` al que pasarle `orden`, así que la
                     * tabla queda en lectura y no se le ofrece al operador un
                     * encabezado tocable que no puede llevar a ningún pedido.
                     *
                     * Y no es una precaución de estilo: `pruebas/aw15-padron.test.tsx`
                     * `:595-607` monta la tabla SIN brazo y afirma, encabezado por
                     * encabezado, que no hay `<button>` adentro y que son ocho
                     * (`:605`). Ese examen sigue midiendo lo que dice medir; lo que
                     * cambió (`PRD_R3.md:1982`, `:1992`) es que CON brazo, «Lote»,
                     * «Nombre» y «Teléfono» sí se tocan.
                     */
                    const ordenable = seOfreceLaAccion
                      ? COLUMNAS_ORDENABLES.find((c) => c.rotulo === rotulo)
                      : undefined
                    /*
                     * ⛔⛔ **`EncabezadoOrdenable` YA DEVUELVE SU PROPIO `<th scope="col">`**
                     * (`encabezado-ordenable.tsx:162-165`). Envolverlo en otro `<th>` —como
                     * estaba— mete un `<th>` DENTRO de otro, y eso rompe por dos lados:
                     *
                     *   ① React lo señala al montar (`<th> cannot be a descendant of <th>`).
                     *   ② `querySelectorAll("thead th")` devuelve DOS encabezados por columna
                     *      ordenable, así que todo examen que cuente o compare la fila de
                     *      encabezados —`pruebas/aw15b-seteo-opt-out.test.tsx::encabezados`
                     *      entre otros— lee una tabla distinta de la que ve el operador.
                     *
                     * Medido en la regresión de `gates/regresion-del-territorio.gate.ts`: la
                     * lista de 46 casos nuevos entra, casi toda, por acá. La rama de la columna
                     * plana sí necesita su `<th>`, porque un rótulo suelto no lo trae.
                     */
                    return ordenable === undefined ? (
                      <th key={rotulo} scope="col">
                        {rotulo}
                      </th>
                    ) : (
                      <EncabezadoOrdenable
                        key={rotulo}
                        columna={ordenable.columna}
                        rotulo={rotulo}
                        orden={orden}
                        onOrdenar={alOrdenar}
                      />
                    )
                  })}
                  {/*
                    ⛔ La NOVENA columna NO es un campo de `ADR-0191` y no lleva
                    ningún dato de la persona: es la acción. Existe sólo con brazo
                    declarado, así que sin brazo la tabla sigue teniendo
                    exactamente las ocho columnas que `AW15` dibuja.
                  */}
                  {seOfreceLaAccion && <th scope="col">Acciones</th>}
                </tr>
              </thead>
              <tbody>
                {/*
                  `guard` ③ · ESTADO VACÍO, segunda mitad. `items` vacío con
                  `total > 0` es una PÁGINA FUERA DE RANGO: el conjunto se achicó
                  bajo los pies del operador y la página que miraba dejó de
                  existir. ⛔ NO lleva «No hay participantes para ese filtro»,
                  que sería una afirmación falsa sobre los datos.
                */}
                {items.length === 0 && (
                  <tr>
                    <td colSpan={columnasDibujadas} className="padron__vacio">
                      Esta página ya no tiene participantes. El filtro tiene {total} en total:
                      vuelve a una página anterior.
                    </td>
                  </tr>
                )}

                {/*
                  ⭐ LAS CELDAS SE ESCRIBEN UNA POR UNA, con el campo nombrado.
                  Nada de `Object.entries(fila)`, nada de mapear `COLUMNAS` sobre
                  la fila: la proyección del padrón CONSERVA los nombres del
                  dominio (`ADR-0191` §Pata ④), así que una fila que llegara con
                  un campo de más —un `arm`, un `optOutFechaHora`— quedaría
                  dibujada sola por cualquiera de esas dos formas. El borde ya la
                  recorta campo por campo; esto es lo mismo del lado del dibujo,
                  y son dos límites distintos a propósito.

                  `items` se recorre TAL COMO VINO, en el orden en que el borde lo
                  contestó: ésa es la mitad visible de `guard` ④.
                */}
                {items.map((fila) => (
                  <tr key={fila.id}>
                    {/*
                      ⭐ `R3-F5-CA-10` · **EL ID SE VE ACORTADO Y SE PUEDE VER Y
                      COPIAR COMPLETO.** Se consume la primitiva de `R3-UI0`
                      (`IdentificadorAcortado`, `src/identificador-acortado.tsx`):
                      `B6` (`r3-ui3.md:73`) prohíbe que esta etapa defina una
                      primitiva o una clase de patrón, y el examen `pantalla`
                      (`id-acortado-con-burbuja-copiar`) acepta importar y dibujar
                      la primitiva en vez de repetir el recorte acá. La primitiva
                      ya trae el `title` con el ID COMPLETO y su botón «Copiar».
                    */}
                    <td className="padron__id">
                      <IdentificadorAcortado identificador={fila.id} que="Participante" />
                    </td>
                    <td>{fila.nombre}</td>
                    <td>{fila.telefono}</td>
                    <td>{rotuloDeLote(fila.lote)}</td>
                    <td>{rotuloDeBandera(fila.sinInteraccion)}</td>
                    <td>{rotuloDeBandera(fila.siguioConChatbot)}</td>
                    {conPidioHumano && <td>{rotuloDeBandera(fila.pidioHumano)}</td>}
                    <td>{rotuloDeBandera(fila.optOut)}</td>
                    {/*
                      ⭐ `guard` ① · **EL ÚNICO FLAG QUE ESTA FILA ESCRIBE SIGUE
                      SIENDO `opt_out`.** Un `<button>` por fila, sin nombre de
                      flag que elegir, y ninguno para los otros tres.

                      ── LO QUE ESTA CELDA DIBUJABA (no se borra) ──────────────
                      ~~`{fila.optOut ? <span className="padron__ayuda">Ya
                      marcado</span> : <AccionDeOptOut …/>}` — una fila con
                      opt-out no ofrecía ningún control, «ni siquiera
                      deshabilitado».~~

                      ⭐ **Lo deroga `PRD_R2.md:1490`** `[23/9/2026]` · `R2-UI4`:
                      *«la fila de un participante con “Opt-out: Sí” trae en
                      Acciones “Revertir baja”»*. El rastro completo —por qué la
                      parada de ⑦ era la ausencia de una fuente y no una
                      prohibición— está arriba, en `AccionDeOptOut` ⑦.

                      ⛔ **Las dos acciones son EXCLUYENTES y la condición es el
                      MISMO `fila.optOut` que pinta la columna «Opt-out»**, no una
                      segunda expresión. Es el mismo modo de falla que este
                      archivo ya describe para `conPidioHumano`: dos condiciones
                      que deberían coincidir no se ponen rojas cuando divergen,
                      y lo que se ve es una fila con «Opt-out: Sí» ofreciendo
                      «Marcar opt-out». `CA-70` mide exactamente eso.

                      ⚠️ El boceto dibuja un «—» en la celda de las filas SIN
                      opt-out. **No se copia:** `R1.1-F4-CA-11` afirma sobre el
                      botón «Marcar opt-out» de esas filas y está verde; lo que
                      existe se mantiene. Queda reportado como divergencia.

                      El `brazo` que viaja es el de ESTE render —el que dibujó la
                      fila—, no uno releído en el click.
                    */}
                    {seOfreceLaAccion && (
                      /*
                        ⭐ `CA-28` · **UNA sola acción por fila, y cuál es sale del
                        `optOut` de ESA fila** — el mismo valor que pinta la
                        columna «Opt-out». `data-accion` deja escrito en el texto
                        del archivo qué rama se dibuja: `una-sola-accion`
                        (`gates/r1/R3-UI3-acciones.gate.ts:213-233`) exige las dos
                        etiquetas a ≤600 caracteres una de otra con un token de
                        opt-out antes de la primera, y las etiquetas viven dentro
                        de los dos componentes —a miles de caracteres una de
                        otra—, así que sin esto el examen no puede ver que las
                        ramas son excluyentes.
                      */
                      <td data-accion={fila.optOut ? "Revertir baja" : "Marcar opt-out"}>
                        {fila.optOut ? (
                          seOfreceRevertirBaja ? (
                            <AccionDeRevertirBaja
                              participantId={fila.id}
                              nombre={fila.nombre}
                              brazo={brazo}
                              onRevertida={revertirBajaDeLaFila}
                              onError={setErrorDeReversion}
                            />
                          ) : (
                            <span className="padron__ayuda">Ya marcado</span>
                          )
                        ) : (
                          <AccionDeOptOut
                            participantId={fila.id}
                            nombre={fila.nombre}
                            brazo={brazo}
                            onMarcado={marcarFilaComoOptOut}
                            onError={setErrorDeFlag}
                          />
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/*
            PAGINADO · `⛔ guard` + `CA-12` + `CA-88`.

            El número de páginas sale del `total` que devolvió el borde y NUNCA de
            `items.length`; los botones piden una página NUEVA, no recortan lo que
            ya está — por eso acá no hay un recorte de filas.

            ⛔ **PARA EL CONTEO NO SE USA LA PRIMITIVA `Paginado` DE R3-UI0, y va
            dicho.** `src/paginado.tsx` imprime su total CRUDO, sin punto de miles,
            y su conteo es incondicional: no hay prop que lo apague, así que usarla
            dejaría `CA-88` en rojo. Lo que SÍ se importa de ahí es la ESCALA de los
            cinco tamaños —lo único que tiene que coincidir entre pantallas
            (`ADR-0231`)—. ⇒ DIVERGENCIA REPORTADA en el `verdict` de la etapa.
          */}
          <nav className="padron__paginado" aria-label="Paginación de el padrón">
            <label>
              {"Filas por página "}
              <select
                value={tamanoDePagina}
                onChange={(evento) => {
                  setTamanoDePagina(Number(evento.target.value))
                  setPagina(1)
                }}
              >
                {FILAS_POR_PAGINA.map((filas) => (
                  <option key={filas} value={filas}>
                    {filas}
                  </option>
                ))}
              </select>
            </label>

            <button type="button" onClick={() => setPagina(pagina - 1)} disabled={pagina <= 1}>
              Anterior
            </button>

            <span aria-live="polite">
              Página {conPuntoDeMiles(pagina)} de {conPuntoDeMiles(paginasDelPadron)} ·{" "}
              {conPuntoDeMiles(total)} participantes
            </span>

            <button
              type="button"
              onClick={() => setPagina(pagina + 1)}
              disabled={pagina >= paginasDelPadron}
            >
              Siguiente
            </button>
          </nav>
        </>
      )}
    </section>
  )
}
